import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import videoCallService from '../services/videoCallService';
import { requestNotificationPermission } from '../utils/notificationHelper';
import ringtone from '../utils/ringtoneHelper';
import { getEcho } from '../echo';

const VideoCallContext = createContext();

const isSameInstantCall = (a, b) => b?.source !== 'scheduled' && String(a?.id) === String(b?.id);
export const useVideoCall = () => {
    const context = useContext(VideoCallContext);
    if (!context) throw new Error('useVideoCall must be used within VideoCallProvider');
    return context;
};

export const VideoCallProvider = ({ children }) => {
    const [incomingCall, setIncomingCall] = useState(null);
    const [activeMeeting, setActiveMeeting] = useState(null);
    const [hasJoined, setHasJoined] = useState(false);
    const [meetingsRefreshTrigger, setMeetingsRefreshTrigger] = useState(0);
    const incomingRef = useRef(null);
    const activeRef = useRef(null);
    const ended = useRef(new Set());
    const pendingJoin = useRef(null);
    const mounted = useRef(true);

    const endCall = useCallback((id) => {
        const key = String(id);
        ended.current.add(key);
        if (String(incomingRef.current?.meeting_id) === key) {
            incomingRef.current = null;
            setIncomingCall(null);
            ringtone.stop();
        }
        if (activeRef.current?.source !== 'scheduled' && String(activeRef.current?.id) === key) {
            activeRef.current = null;
            setActiveMeeting(null);
            setHasJoined(false);
        }
        setMeetingsRefreshTrigger(n => n + 1);
    }, []);

    useEffect(() => {
        mounted.current = true;
        if (localStorage.getItem('user')) requestNotificationPermission().catch(() => {});
        return () => { mounted.current = false; ringtone.stop(); };
    }, []);

    useEffect(() => {
        let user;
        try { user = JSON.parse(localStorage.getItem('user') || '{}'); } catch { return; }
        if (!user?.id) return;
        let channel;
        let timeout;
        let attempts = 0;
        const receiveCall = (event) => {
            if (ended.current.has(String(event.meeting_id))) return;
            if (Date.parse(event.expires_at) <= Date.now()) return;
            if (activeRef.current || pendingJoin.current) return;
            // Do not replace an invitation that the user is already answering.
            if (incomingRef.current && String(incomingRef.current.meeting_id) !== String(event.meeting_id)) return;
            const call = { ...event, call_type: event.call_type || 'video' };
            incomingRef.current = call;
            setIncomingCall(call);
            setMeetingsRefreshTrigger(n => n + 1);
        };
        const receiveEnd = (event) => endCall(event.meeting_id);
        const subscribe = () => {
            const echo = getEcho();
            if (echo) {
                window.Echo = echo;
                channel = echo.private(`user.${user.id}`);
                channel.listen('.incoming-call', receiveCall);
                channel.listen('.call-ended', receiveEnd);
            } else if (++attempts < 20) timeout = setTimeout(subscribe, 500);
        };
        subscribe();
        return () => {
            clearTimeout(timeout);
            // Other features share this private channel; remove only our listeners.
            channel?.stopListening('.incoming-call', receiveCall);
            channel?.stopListening('.call-ended', receiveEnd);
        };
    }, [endCall]);

    useEffect(() => {
        const id = incomingCall?.meeting_id ?? (activeMeeting?.source !== 'scheduled' ? activeMeeting?.id : null);
        if (!id) return;
        let cancelled = false;
        let checking = false;
        const reconcile = async () => {
            if (checking) return;
            checking = true;
            try {
                const response = await videoCallService.show(id);
                const meeting = response?.data?.meeting;
                if (!cancelled && meeting?.status === 'ended') endCall(id);
            } catch (error) {
                if (!cancelled && error?.status === 404) endCall(id);
            } finally { checking = false; }
        };
        const timer = setInterval(reconcile, 3000);
        return () => { cancelled = true; clearInterval(timer); };
    }, [incomingCall?.meeting_id, activeMeeting?.id, activeMeeting?.source, endCall]);

    const handleAcceptCall = async (expectedId) => {
        const call = incomingRef.current;
        if (!call || pendingJoin.current || (expectedId != null && String(expectedId) !== String(call.meeting_id))) return;
        const id = call.meeting_id;
        pendingJoin.current = id;
        incomingRef.current = null;
        setIncomingCall(null);
        ringtone.stop();
        try {
            const response = await videoCallService.join(id);
            if (mounted.current && !ended.current.has(String(id)) && response.status) {
                const meeting = { ...response.data.meeting, tokens: response.data.tokens };
                activeRef.current = meeting;
                setActiveMeeting(meeting);
                setHasJoined(true);
            }
        } catch { /* API interceptor displays request failures. */ }
        finally { pendingJoin.current = null; }
    };

    const handleRejectCall = async (expectedId) => {
        const call = incomingRef.current;
        if (!call || (expectedId != null && String(expectedId) !== String(call.meeting_id))) return;
        incomingRef.current = null;
        setIncomingCall(null);
        ringtone.stop();
        try { await videoCallService.reject(call.meeting_id); }
        catch { /* API interceptor displays request failures. */ }
    };

    const handleLeaveMeeting = async () => {
        const meeting = activeRef.current;
        if (!meeting) return;
        activeRef.current = null;
        setActiveMeeting(null);
        setHasJoined(false);
        if (meeting.source !== 'scheduled') {
            try { await videoCallService.leave(meeting.id); }
            catch { /* Local media teardown must still complete on network failure. */ }
        }
    };

    const joinMeeting = (meeting) => {
        if (meeting?.source !== 'scheduled' && ended.current.has(String(meeting?.id))) return;
        const previous = activeRef.current;
        activeRef.current = meeting;
        setActiveMeeting(meeting);
        // Instant calls are already joined on the server; scheduled meetings still show the preview lobby.
        setHasJoined(!!meeting?.tokens && meeting?.source !== 'scheduled');
        if (previous && previous.source !== 'scheduled' && !isSameInstantCall(previous, meeting)) {
            // Release the replaced call so the server does not keep this user in it.
            // Never let this cleanup block or break opening the new call.
            Promise.resolve().then(() => videoCallService.leave(previous.id)).catch(() => {});
        }
    };

    return <VideoCallContext.Provider value={{ incomingCall, activeMeeting, hasJoined,
        meetingsRefreshTrigger, handleAcceptCall, handleRejectCall, handleLeaveMeeting, joinMeeting }}>
        {children}
    </VideoCallContext.Provider>;
};
